import { MessageSquareWarning } from 'lucide-react';
import { useState } from 'react';

import {
  BasicTableSettings,
  RefreshableTableSettings,
  createPersistedStore,
  refreshableSettings,
} from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import {
  Datatable,
  TableSettingsMenu,
  TableSettingsMenuAutoRefresh,
} from '@@/datatables';
import { Modal } from '@@/modals';

import { UseCombinedAlertSettings } from '../AlertSettingsDatatable/useAlertSettings';
import { AlertSettingsRow } from '../AlertSettingsDatatable/types';

import { createColumns } from './columns';
import { useAlertRules, useUpdateAlertRule } from './useAlertRules';
import { transformToAlertRuleRow } from './utils';
import { AlertRuleRow } from './types';
import { AlertRuleEditForm } from './AlertRuleEditForm';

interface TableSettings extends BasicTableSettings, RefreshableTableSettings {}

const storageKey = 'portainer.alert-rules';
const settingsStore = createPersistedStore<TableSettings>(
  storageKey,
  'name',
  (set) => ({
    ...refreshableSettings(set, 10 * 1000),
  })
);

export function AlertRulesDatatable() {
  const [editingAlertRule, setEditingAlertRule] = useState<AlertRuleRow | null>(
    null
  );
  const [togglingId, setTogglingId] = useState<number | undefined>(undefined);
  const tableState = useTableState(settingsStore, storageKey);
  const alertRulesQuery = useAlertRules({
    refetchInterval: tableState.autoRefreshRate
      ? tableState.autoRefreshRate * 1000
      : undefined,
    select: transformToAlertRuleRow,
  });

  const updateAlertRule = useUpdateAlertRule();

  function handleEdit(alertRule: AlertRuleRow) {
    setEditingAlertRule(alertRule);
  }

  function handleSave() {
    setEditingAlertRule(null);
  }

  function handleCancel() {
    setEditingAlertRule(null);
  }

  function handleToggle(alertRule: AlertRuleRow, enabled: boolean) {
    setTogglingId(alertRule.id);
    updateAlertRule.mutate(
      { ...alertRule, enabled },
      {
        onSuccess: () => {
          setTogglingId(undefined);
        },
        onError: () => {
          setTogglingId(undefined);
        },
      }
    );
  }

  const alertSettings = UseCombinedAlertSettings({
    select: (data: AlertSettingsRow[]) =>
      data.some((setting: AlertSettingsRow) => setting.enabled),
  });

  const columns = createColumns(
    handleEdit,
    handleToggle,
    togglingId,
    alertSettings.data
  );

  return (
    <>
      <Datatable
        dataset={alertRulesQuery.data || []}
        columns={columns}
        isLoading={alertRulesQuery.isLoading}
        settingsManager={tableState}
        emptyContentLabel="No alert rules found"
        title="Alert Rules"
        titleIcon={MessageSquareWarning}
        disableSelect
        renderTableSettings={() => (
          <TableSettingsMenu>
            <TableSettingsMenuAutoRefresh
              onChange={tableState.setAutoRefreshRate}
              value={tableState.autoRefreshRate}
            />
          </TableSettingsMenu>
        )}
        data-cy="alert-rules-datatable"
      />

      {editingAlertRule && (
        <Modal onDismiss={handleCancel} size="lg">
          <AlertRuleEditForm
            alertRule={editingAlertRule}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        </Modal>
      )}
    </>
  );
}
